// frontend/src/features/viewer/McBimItemDetailsTable.tsx
import "ag-grid-enterprise";
import React, { useMemo, useState } from "react";
import { useAppSelector } from "../../app/hooks";
import { Box, TextInput } from "@mantine/core";
import { AgGridReact } from "ag-grid-react";
import type { ColDef, ColSpanParams, ICellRendererParams } from "ag-grid-enterprise";

import { useAgGridReduxState } from "../../hooks/useAgGridReduxState";
import { selectAgGridStateByKey } from "../../agGridStateSlice";
import { selectMcBimItemFlatRows } from "./singleMcbimItemSelectors";
import VSpace from "../../VSpace";
import styles from "./McBimItemDetailsTable.module.css";

type DetailsRow = { label: string; value: string };

const DetailCell = (params: ICellRendererParams<DetailsRow>) => (
  <div className={styles.detailCell}>{params.value || "—"}</div>
);

const SectionCell = (params: ICellRendererParams<DetailsRow>) => (
  <div className={styles.sectionCell}>{params.value}</div>
);

export const McBimItemDetailsTable: React.FC = () => {
  const rows = useAppSelector(selectMcBimItemFlatRows);

  const columnDefs = useMemo<ColDef[]>(
    () => [
      {
        field: "label",
        headerName: "Label",
        flex: 1,
        wrapText: true,
        autoHeight: true,
        cellRendererSelector: (params) =>
          params.data?.value === ""
            ? { component: SectionCell }
            : { component: DetailCell },
        colSpan: (params: ColSpanParams<DetailsRow>) => (params.data?.value === "" ? 2 : 1),
        cellClassRules: {
          "section-header": (params) => params.data?.value === "",
        },
        cellStyle: (params) => {
          if (params.data?.value === "") {
            return { fontWeight: "bold", backgroundColor: "#f8f9fa", textAlign: "center" };
          }
          return null;
        },
      },
      {
        field: "value",
        headerName: "Verdi",
        flex: 2,
        wrapText: true,
        autoHeight: true,
        cellRenderer: DetailCell,
      },
    ],
    []
  );

  const defaultColDef = useMemo<ColDef>(
    () => ({
      resizable: true,
      sortable: false,
      filter: true,
    }),
    []
  );

  const gridKey = "mcBimDetailsTable";
  const saved = useAppSelector(selectAgGridStateByKey(gridKey));
  const [search, setSearch] = useState(saved?.quickFilter ?? "");

  const { gridStateHandlers, setQuickFilterText } = useAgGridReduxState(gridKey, {
    availableColIds: ["label", "value"],
    columnsSignature: "label|value",
    managePivotMode: false,
  });

  return (
    <Box className={`ag-theme-quartz ${styles.detailsTable}`} style={{ height: "100%", width: "100%" }}>
      <TextInput
        size="xs"
        placeholder="Søk i detaljer…"
        value={search}
        onChange={(e) => {
          const v = e.currentTarget.value;
          setSearch(v);
          setQuickFilterText(v);
        }}
      />
      <VSpace />
      <Box className={styles.gridShell}>
        <AgGridReact
          rowData={rows}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          animateRows
          overlayNoRowsTemplate="<span style='padding:8px;'>Ingen data</span>"
          {...gridStateHandlers}
        />
      </Box>
    </Box>
  );
};
